import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiDelete } from '../api/http';
import './CommentBox.css';

export default function CommentBox({ projectId, canDelete }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  // ✅ Fetch comments on mount
  useEffect(() => {
    fetchComments();
  }, [projectId]);

  async function fetchComments() {
    setLoading(true);
    try {
      const data = await apiGet(`/api/projects/${projectId}/comments`);
      if (Array.isArray(data)) {
        setComments(data);
      } else {
        setComments([]);
      }
    } catch (err) {
      setComments([]);
    } finally {
      setLoading(false);
    }
  }

  async function handlePost() {
    if (!text.trim()) return;
    setPosting(true);
    try {
      await apiPost(`/api/projects/${projectId}/comments`, { text: text.trim() });
      setText('');
      await fetchComments(); // ✅ refresh after posting
    } catch (err) {
      alert('Failed to post comment');
    } finally {
      setPosting(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Are you sure you want to delete this comment?')) return;
    setDeletingId(id);
    try {
      await apiDelete(`/api/projects/${projectId}/comments/${id}`);
      await fetchComments(); // ✅ refresh after deleting
    } catch (err) {
      alert('Failed to delete comment');
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <div className="comment-box card">
      <h3>Comments</h3>

      <div className="comment-input-row">
        <textarea
          className="comment-textarea"
          value={text}
          onChange={(e) => setText(e.target.value)}
          rows={3}
          placeholder="Write a comment..."
        />
        <button
          className="btn primary"
          disabled={!text.trim() || posting}
          onClick={handlePost}
        >
          {posting ? 'Posting...' : 'Post'}
        </button>
      </div>

      {loading ? (
        <p className="empty-state">Loading comments...</p>
      ) : comments.length === 0 ? (
        <p className="empty-state">No comments yet.</p>
      ) : (
        <ul className="comment-list">
          {comments
            .filter((c) => !c.is_deleted)
            .map((c) => (
              <li key={c.id} className="comment-item">
                <div className="comment-body">
                  <div className="comment-text">{c.text}</div>
                  <small className="comment-meta">
                    by {c.user_name} ·{' '}
                    {c.created_at ? new Date(c.created_at).toLocaleString() : 'unknown time'}
                  </small>
                </div>
                {canDelete && (
                  <button
                    className="btn secondary small"
                    disabled={deletingId === c.id}
                    onClick={() => handleDelete(c.id)}
                  >
                    {deletingId === c.id ? 'Deleting...' : 'Delete'}
                  </button>
                )}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
}
